/**
 * Secure Logger - Evita la exposición de información sensible en logs
 * Cumple con las mejores prácticas de seguridad para logging
 */

const SENSITIVE_KEYS = [
  'password', 'token', 'secret', 'key', 'auth', 'credential',
  'email', 'username', 'phone', 'ssn', 'credit', 'card',
  'access_token', 'refresh_token', 'id_token', 'session'
];

const LOG_LEVELS = {
  ERROR: 0,
  WARN: 1,
  INFO: 2,
  DEBUG: 3
};

class SecureLogger {
  constructor() {
    this.level = import.meta.env.PROD ? LOG_LEVELS.ERROR : LOG_LEVELS.DEBUG;
    this.enableConsole = !import.meta.env.PROD;
  }

  /**
   * Sanitiza objetos removiendo información sensible
   */
  sanitizeData(data) {
    if (typeof data !== 'object' || data === null) {
      return data;
    }

    if (Array.isArray(data)) {
      return data.map(item => this.sanitizeData(item));
    }

    const sanitized = {};
    for (const [key, value] of Object.entries(data)) {
      const keyLower = key.toLowerCase();
      const isSensitive = SENSITIVE_KEYS.some(sensitiveKey => 
        keyLower.includes(sensitiveKey)
      );

      if (isSensitive) {
        sanitized[key] = '[REDACTED]';
      } else if (typeof value === 'object') {
        sanitized[key] = this.sanitizeData(value);
      } else {
        sanitized[key] = value;
      }
    }

    return sanitized;
  }

  /**
   * Formatea el mensaje de log de manera segura
   */
  formatMessage(level, message, data = null) {
    const timestamp = new Date().toISOString();
    const sanitizedData = data ? this.sanitizeData(data) : null;
    
    return {
      timestamp,
      level,
      message,
      ...(sanitizedData && { data: sanitizedData })
    };
  }

  /**
   * Log de error (siempre se muestra)
   */
  error(message, data = null) {
    const logEntry = this.formatMessage('ERROR', message, data);
    
    if (this.enableConsole) {
      console.error('❌', message, data ? this.sanitizeData(data) : '');
    }
    
    // En producción, aquí se enviaría a un servicio de logging externo
    return logEntry;
  }

  /**
   * Log de advertencia
   */
  warn(message, data = null) {
    if (this.level < LOG_LEVELS.WARN) return;
    
    const logEntry = this.formatMessage('WARN', message, data);
    
    if (this.enableConsole) {
      console.warn('⚠️', message, data ? this.sanitizeData(data) : '');
    }
    
    return logEntry;
  }

  /**
   * Log informativo
   */
  info(message, data = null) {
    if (this.level < LOG_LEVELS.INFO) return;
    
    const logEntry = this.formatMessage('INFO', message, data);
    
    if (this.enableConsole) {
      console.info('ℹ️', message, data ? this.sanitizeData(data) : '');
    }
    
    return logEntry;
  }

  /**
   * Log de debug (solo en desarrollo)
   */
  debug(message, data = null) {
    if (this.level < LOG_LEVELS.DEBUG) return;
    
    const logEntry = this.formatMessage('DEBUG', message, data);
    
    if (this.enableConsole) {
      console.log('🔍', message, data ? this.sanitizeData(data) : '');
    }
    
    return logEntry;
  }

  /**
   * Log de éxito
   */
  success(message, data = null) {
    if (this.level < LOG_LEVELS.INFO) return;
    
    const logEntry = this.formatMessage('SUCCESS', message, data);
    
    if (this.enableConsole) {
      console.log('✅', message, data ? this.sanitizeData(data) : '');
    }
    
    return logEntry;
  }
}

// Instancia singleton
const logger = new SecureLogger();

export default logger;
